import * as THREE from 'three';
import { MapTextures } from './MapTextures';

export interface JumpPad {
  position: THREE.Vector3;
  targetVelocity: THREE.Vector3;
  mesh: THREE.Group;
  radius: number;
}

export interface PickupItem {
  id: string;
  type: 'health_pack' | 'health_vial' | 'armor' | 'shield_belt' | 'weapon_shock' | 'weapon_rocket' | 'weapon_flak' | 'weapon_sniper';
  position: THREE.Vector3;
  mesh: THREE.Group;
  respawnTime: number;
  timer: number;
  active: boolean;
}

export interface MapCollider {
  box: THREE.Box3;
  type: 'floor' | 'wall';
}

export class MorpheusMap {
  public group: THREE.Group = new THREE.Group();
  public colliders: MapCollider[] = [];
  public jumpPads: JumpPad[] = [];
  public pickups: PickupItem[] = [];
  public spawnPoints: THREE.Vector3[] = [];
  public waypoints: THREE.Vector3[] = [];

  // Animated elements
  private jumpPadRings: THREE.Mesh[] = [];
  private pickupMeshes: THREE.Group[] = [];
  private clouds: THREE.Points;
  private time: number = 0;

  // Procedural map textures
  private floorTextureBase!: THREE.CanvasTexture;
  private wallTextureBase!: THREE.CanvasTexture;
  private helipadTexture!: THREE.CanvasTexture;
  private skybridgeTexture!: THREE.CanvasTexture;
  private hazardTextureBase!: THREE.CanvasTexture;

  constructor() {
    this.initTextures();
    this.buildSkyAndAtmosphere();
    this.buildTowers();
    this.buildJumpPads();
    this.buildPickups();
    this.setupWaypointsAndSpawns();
    this.clouds = this.buildDistantClouds();
  }

  private initTextures() {
    this.floorTextureBase = MapTextures.createFloorGrateTexture();
    this.wallTextureBase = MapTextures.createSkyscraperWallTexture();
    this.helipadTexture = MapTextures.createHelipadTexture();
    this.skybridgeTexture = MapTextures.createSkybridgeTexture();
    this.hazardTextureBase = MapTextures.createHazardStripeTexture();
  }

  private getClonedTexture(base: THREE.CanvasTexture, repeatX: number, repeatY: number): THREE.CanvasTexture {
    const tex = base.clone();
    tex.repeat.set(repeatX, repeatY);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.needsUpdate = true;
    return tex;
  }

  private buildSkyAndAtmosphere() {
    // Distant futuristic city grid far below (y = -120)
    const cityFloorGeo = new THREE.PlaneGeometry(800, 800);
    const cityFloorMat = new THREE.MeshBasicMaterial({
      color: 0x050814,
    });
    const cityFloor = new THREE.Mesh(cityFloorGeo, cityFloorMat);
    cityFloor.position.y = -120;
    cityFloor.rotation.x = -Math.PI / 2;
    this.group.add(cityFloor);

    // Distant skyscraper spires poking through the clouds
    const spireMat = new THREE.MeshBasicMaterial({ color: 0x0a1020 });
    for (let i = 0; i < 40; i++) {
      const h = 50 + Math.random() * 80;
      const w = 8 + Math.random() * 16;
      const x = (Math.random() - 0.5) * 600;
      const z = (Math.random() - 0.5) * 600;
      // Skip area right under the towers
      if (Math.abs(x) < 70 && Math.abs(z) < 70) continue;

      const spire = new THREE.Mesh(new THREE.BoxGeometry(w, h, w), spireMat);
      spire.position.set(x, -120 + h / 2, z);
      this.group.add(spire);
    }
  }

  private addCollider(x: number, y: number, z: number, w: number, h: number, d: number, type: 'floor' | 'wall' = 'wall') {
    const min = new THREE.Vector3(x - w / 2, y - h / 2, z - d / 2);
    const max = new THREE.Vector3(x + w / 2, y + h / 2, z + d / 2);
    this.colliders.push({
      box: new THREE.Box3(min, max),
      type,
    });
  }

  private buildTowers() {
    // High-tech sci-fi materials with clear surface definition & lighting response
    const trimMat = new THREE.MeshBasicMaterial({ color: 0x00fff2, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
    const trimOrangeMat = new THREE.MeshBasicMaterial({ color: 0xffaa00, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
    const windowMatWarm = new THREE.MeshBasicMaterial({ color: 0xffd166, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
    const windowMatCyan = new THREE.MeshBasicMaterial({ color: 0x38bdf8, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });

    // Procedural textured materials with dedicated UV repeat scaling
    const t1ShaftMat = new THREE.MeshStandardMaterial({
      map: this.getClonedTexture(this.wallTextureBase, 4, 16),
      metalness: 0.5,
      roughness: 0.45,
    });
    const t1RoofMat = new THREE.MeshStandardMaterial({
      map: this.getClonedTexture(this.floorTextureBase, 8, 8),
      metalness: 0.6,
      roughness: 0.35,
    });
    const padSideMat = new THREE.MeshStandardMaterial({
      map: this.getClonedTexture(this.hazardTextureBase, 8, 1),
      metalness: 0.5,
      roughness: 0.4,
    });
    const padTopMat = new THREE.MeshStandardMaterial({
      map: this.helipadTexture,
      metalness: 0.4,
      roughness: 0.4,
    });
    const padBottomMat = new THREE.MeshStandardMaterial({
      color: 0x334155,
      metalness: 0.5,
      roughness: 0.5,
    });

    const t2ShaftMat = new THREE.MeshStandardMaterial({
      map: this.getClonedTexture(this.wallTextureBase, 3.5, 16),
      metalness: 0.5,
      roughness: 0.45,
    });
    const t2RoofMat = new THREE.MeshStandardMaterial({
      map: this.getClonedTexture(this.floorTextureBase, 6.5, 6.5),
      metalness: 0.6,
      roughness: 0.35,
    });
    const shelterMat = new THREE.MeshStandardMaterial({
      map: this.getClonedTexture(this.wallTextureBase, 2, 1.5),
      metalness: 0.5,
      roughness: 0.45,
    });

    const t3ShaftMat = new THREE.MeshStandardMaterial({
      map: this.getClonedTexture(this.wallTextureBase, 3.5, 16),
      metalness: 0.5,
      roughness: 0.45,
    });
    const t3RoofMat = new THREE.MeshStandardMaterial({
      map: this.getClonedTexture(this.floorTextureBase, 7, 7),
      metalness: 0.6,
      roughness: 0.35,
    });
    const canopyMat = new THREE.MeshStandardMaterial({
      map: this.getClonedTexture(this.floorTextureBase, 3.5, 4.5),
      metalness: 0.6,
      roughness: 0.35,
    });

    const bridgeMat = new THREE.MeshStandardMaterial({
      map: this.getClonedTexture(this.skybridgeTexture, 6, 1),
      metalness: 0.65,
      roughness: 0.35,
    });

    // ==========================================
    // TOWER 1 (Alpha / Central Tower - Main Hub)
    // ==========================================
    const t1 = new THREE.Group();
    t1.position.set(0, 0, 0);

    // Tower 1 Shaft (From deep below up to rooftop base at y = 24.0)
    const t1Shaft = new THREE.Mesh(new THREE.BoxGeometry(32, 140, 32), t1ShaftMat);
    t1Shaft.position.set(0, -46, 0);
    t1.add(t1Shaft);

    // Glowing window strips on Tower 1 facade
    [-16.15, 16.15].forEach((z) => {
      for (let y = -90; y < 20; y += 12) {
        const win = new THREE.Mesh(new THREE.PlaneGeometry(24, 2.5), (y % 24 === 0) ? windowMatCyan : windowMatWarm);
        win.position.set(0, y, z);
        if (z < 0) win.rotation.y = Math.PI;
        t1.add(win);
      }
    });

    // Tower 1 Roof Floor (y = 25, h = 2 -> from y = 24.0 to y = 26.0)
    const t1Roof = new THREE.Mesh(new THREE.BoxGeometry(32, 2, 32), t1RoofMat);
    t1Roof.position.set(0, 25, 0);
    t1.add(t1Roof);
    this.addCollider(0, 25, 0, 32, 2, 32, 'floor');

    // Roof Center Helipad / Weapon Platform (y = 26.2, h = 0.4 -> from y = 26.0 to y = 26.4)
    const t1Pad = new THREE.Mesh(new THREE.CylinderGeometry(8, 8.5, 0.4, 24), [padSideMat, padTopMat, padBottomMat]);
    t1Pad.position.set(0, 26.2, 0);
    t1.add(t1Pad);
    this.addCollider(0, 26.2, 0, 16, 0.4, 16, 'floor');

    // Roof Neon Ring Trim (raised slightly above helipad to eliminate Z-fighting)
    const ringGeo = new THREE.RingGeometry(7.6, 8.2, 32);
    const ringMesh = new THREE.Mesh(ringGeo, trimOrangeMat);
    ringMesh.rotation.x = -Math.PI / 2;
    ringMesh.position.set(0, 26.43, 0);
    t1.add(ringMesh);

    // Roof Parapet / Low Railings with jump gaps
    this.createRailSection(t1, -10, 26.6, -15.5, 10, 1.2, 1);
    this.createRailSection(t1, 10, 26.6, -15.5, 10, 1.2, 1);
    this.createRailSection(t1, -10, 26.6, 15.5, 10, 1.2, 1);
    this.createRailSection(t1, 10, 26.6, 15.5, 10, 1.2, 1);
    this.createRailSection(t1, -15.5, 26.6, 0, 1, 1.2, 20);
    this.createRailSection(t1, 15.5, 26.6, 0, 1, 1.2, 20);

    // Tower 1 Spire Antennas with blinking beacon tops
    const spireMat = new THREE.MeshStandardMaterial({ color: 0x8a99ad, metalness: 0.9, roughness: 0.2 });
    const spire1 = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.8, 16, 8), spireMat);
    spire1.position.set(-14, 34, -14);
    const beacon1 = new THREE.Mesh(new THREE.SphereGeometry(0.4, 8, 8), new THREE.MeshBasicMaterial({ color: 0xff3300 }));
    beacon1.position.y = 8;
    spire1.add(beacon1);
    t1.add(spire1);

    const spire2 = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 0.8, 16, 8), spireMat);
    spire2.position.set(14, 34, 14);
    const beacon2 = new THREE.Mesh(new THREE.SphereGeometry(0.4, 8, 8), new THREE.MeshBasicMaterial({ color: 0x00fff2 }));
    beacon2.position.y = 8;
    spire2.add(beacon2);
    t1.add(spire2);

    // ==========================================
    // TOWER 2 (Beta Tower - North East)
    // ==========================================
    const t2 = new THREE.Group();
    const t2Pos = new THREE.Vector3(48, 0, -42);
    t2.position.copy(t2Pos);

    // Tower 2 Shaft (top ends at y = 19.0 to seat roof cleanly)
    const t2Shaft = new THREE.Mesh(new THREE.BoxGeometry(26, 140, 26), t2ShaftMat);
    t2Shaft.position.set(0, -51, 0);
    t2.add(t2Shaft);

    // Tower 2 Window strips
    [-13.1, 13.1].forEach((x) => {
      for (let y = -90; y < 15; y += 14) {
        const win = new THREE.Mesh(new THREE.PlaneGeometry(18, 2.5), (y % 28 === 0) ? windowMatCyan : windowMatWarm);
        win.position.set(x, y, 0);
        win.rotation.y = x > 0 ? Math.PI / 2 : -Math.PI / 2;
        t2.add(win);
      }
    });

    // Tower 2 Roof (y = 20, thickness 2 -> top surface is exactly y = 21.0)
    const t2Roof = new THREE.Mesh(new THREE.BoxGeometry(26, 2, 26), t2RoofMat);
    t2Roof.position.set(0, 20, 0);
    t2.add(t2Roof);
    this.addCollider(t2Pos.x, 20, t2Pos.z, 26, 2, 26, 'floor');

    // Tower 2 Perimeter Railings (with openings for jump pads)
    this.createRailSection(t2, -8, 21.6, -12.5, 8, 1.2, 1);
    this.createRailSection(t2, 8, 21.6, -12.5, 8, 1.2, 1);
    this.createRailSection(t2, 0, 21.6, 12.5, 24, 1.2, 1);
    this.createRailSection(t2, 12.5, 21.6, 0, 1, 1.2, 24);

    // Tower 2 Rooftop Shelter & Catwalk (y = 24, h = 6 -> top surface is exactly y = 27.0)
    const shelter = new THREE.Mesh(new THREE.BoxGeometry(8, 6, 8), shelterMat);
    shelter.position.set(-6, 24, 0);
    t2.add(shelter);
    this.addCollider(t2Pos.x - 6, 24, t2Pos.z, 8, 6, 8, 'wall');
    this.addCollider(t2Pos.x - 6, 26.5, t2Pos.z, 8, 1, 8, 'floor'); // top is y = 27.0 exactly

    // ==========================================
    // TOWER 3 (Gamma Tower - South West)
    // ==========================================
    const t3 = new THREE.Group();
    const t3Pos = new THREE.Vector3(-46, 0, 44);
    t3.position.copy(t3Pos);

    // Tower 3 Shaft (top ends at y = 15.0 to seat roof cleanly)
    const t3Shaft = new THREE.Mesh(new THREE.BoxGeometry(28, 140, 28), t3ShaftMat);
    t3Shaft.position.set(0, -55, 0);
    t3.add(t3Shaft);

    // Tower 3 Window strips
    [-14.1, 14.1].forEach((z) => {
      for (let y = -90; y < 10; y += 14) {
        const win = new THREE.Mesh(new THREE.PlaneGeometry(20, 2.5), (y % 28 === 0) ? windowMatCyan : windowMatWarm);
        win.position.set(0, y, z);
        if (z < 0) win.rotation.y = Math.PI;
        t3.add(win);
      }
    });

    // Tower 3 Roof (y = 16, thickness 2 -> top surface is exactly y = 17.0)
    const t3Roof = new THREE.Mesh(new THREE.BoxGeometry(28, 2, 28), t3RoofMat);
    t3Roof.position.set(0, 16, 0);
    t3.add(t3Roof);
    this.addCollider(t3Pos.x, 16, t3Pos.z, 28, 2, 28, 'floor');

    // Tower 3 Perimeter Railings (with openings for jump pads)
    this.createRailSection(t3, -9, 17.6, -13.5, 9, 1.2, 1);
    this.createRailSection(t3, 9, 17.6, -13.5, 9, 1.2, 1);
    this.createRailSection(t3, 0, 17.6, 13.5, 26, 1.2, 1);
    this.createRailSection(t3, 13.5, 17.6, 0, 1, 1.2, 26);

    // Slanted upper canopy with walk collider
    const canopy = new THREE.Mesh(new THREE.BoxGeometry(14, 1, 18), canopyMat);
    canopy.position.set(6, 23, 0);
    canopy.rotation.z = -0.2;
    t3.add(canopy);
    this.addCollider(t3Pos.x + 6, 23, t3Pos.z, 14, 1, 18, 'floor');

    // Connecting High-Tension Skybridge Beam between Tower 1 and Tower 2
    const bridgeGeo = new THREE.BoxGeometry(32, 1.2, 3);
    const bridge = new THREE.Mesh(bridgeGeo, bridgeMat);
    bridge.position.set(24, 19, -21);
    bridge.rotation.y = Math.atan2(-42, 48);
    this.group.add(bridge);
    this.addCollider(24, 19, -21, 24, 1.2, 4, 'floor');

    this.group.add(t1);
    this.group.add(t2);
    this.group.add(t3);
  }

  private createRailSection(parent: THREE.Group, x: number, y: number, z: number, w: number, h: number, d: number) {
    const repeatU = Math.max(1, Math.round(Math.max(w, d) / 3));
    const railMat = new THREE.MeshStandardMaterial({
      map: this.getClonedTexture(this.hazardTextureBase, repeatU, 1),
      metalness: 0.6,
      roughness: 0.35,
    });
    const rail = new THREE.Mesh(new THREE.BoxGeometry(w, h, d), railMat);
    rail.position.set(x, y, z);
    parent.add(rail);

    const worldPos = new THREE.Vector3();
    rail.getWorldPosition(worldPos);
    this.addCollider(parent.position.x + x, y, parent.position.z + z, w, h, d, 'wall');
  }

  private buildJumpPads() {
    // Jump Pad 1: Tower 1 (Center) -> Tower 2 (North East)
    this.createJumpPad(
      new THREE.Vector3(12, 26.2, -12),
      new THREE.Vector3(26, 18, -22), // Powerful low-g arc
      0x00fff2
    );

    // Jump Pad 2: Tower 1 (Center) -> Tower 3 (South West)
    this.createJumpPad(
      new THREE.Vector3(-12, 26.2, 12),
      new THREE.Vector3(-24, 18, 22),
      0xff6600
    );

    // Jump Pad 3: Tower 2 (North East) -> Tower 1 (Center)
    this.createJumpPad(
      new THREE.Vector3(38, 21.2, -32),
      new THREE.Vector3(-28, 20, 24),
      0x00fff2
    );

    // Jump Pad 4: Tower 2 (North East) -> Tower 3 (South West cross-launch!)
    this.createJumpPad(
      new THREE.Vector3(42, 21.2, -48),
      new THREE.Vector3(-44, 25, 46),
      0xff007f
    );

    // Jump Pad 5: Tower 3 (South West) -> Tower 1 (Center)
    this.createJumpPad(
      new THREE.Vector3(-36, 17.2, 34),
      new THREE.Vector3(28, 22, -24),
      0xff6600
    );

    // Jump Pad 6: Tower 3 (South West) -> Tower 2 (North East cross-launch!)
    this.createJumpPad(
      new THREE.Vector3(-48, 17.2, 46),
      new THREE.Vector3(44, 26, -46),
      0xff007f
    );
  }

  private createJumpPad(pos: THREE.Vector3, targetVelocity: THREE.Vector3, colorHex: number) {
    const padGroup = new THREE.Group();
    padGroup.position.copy(pos);

    // Base plate with hazard striped perimeter rim
    const baseGeo = new THREE.CylinderGeometry(2.2, 2.5, 0.3, 16);
    const padSideMat = new THREE.MeshStandardMaterial({
      map: this.getClonedTexture(this.hazardTextureBase, 4, 1),
      metalness: 0.6,
      roughness: 0.4,
    });
    const padTopMat = new THREE.MeshStandardMaterial({
      color: 0x111827,
      metalness: 0.8,
      roughness: 0.3,
    });
    const base = new THREE.Mesh(baseGeo, [padSideMat, padTopMat, padTopMat]);
    padGroup.add(base);

    // Glowing Emitter Disc
    const discGeo = new THREE.RingGeometry(0.8, 1.8, 16);
    const discMat = new THREE.MeshBasicMaterial({
      color: colorHex,
      side: THREE.DoubleSide,
    });
    const disc = new THREE.Mesh(discGeo, discMat);
    disc.rotation.x = -Math.PI / 2;
    disc.position.y = 0.16;
    padGroup.add(disc);
    this.jumpPadRings.push(disc);

    // Upward Chevron / Arrow Mesh
    const arrowGeo = new THREE.ConeGeometry(0.6, 1.2, 8);
    const arrowMat = new THREE.MeshBasicMaterial({ color: colorHex });
    const arrow = new THREE.Mesh(arrowGeo, arrowMat);
    arrow.position.set(0, 0.8, 0);
    padGroup.add(arrow);

    this.group.add(padGroup);

    this.jumpPads.push({
      position: pos.clone(),
      targetVelocity: targetVelocity.clone(),
      mesh: padGroup,
      radius: 2.8,
    });
  }

  private buildPickups() {
    // Weapons and Pickups distributed tactically across the 3 towers
    this.addPickup('health_1', 'health_pack', new THREE.Vector3(0, 28, 0), 0x39ff14);
    this.addPickup('rocket_1', 'weapon_rocket', new THREE.Vector3(0, 27.5, 8), 0xff3300);
    this.addPickup('shock_1', 'weapon_shock', new THREE.Vector3(48, 22.5, -42), 0x00aaff);
    this.addPickup('flak_1', 'weapon_flak', new THREE.Vector3(54, 22.5, -34), 0xffaa00);
    this.addPickup('sniper_1', 'weapon_sniper', new THREE.Vector3(-46, 18.5, 44), 0x00ffff);
    this.addPickup('armor_1', 'armor', new THREE.Vector3(-38, 18.5, 52), 0xbf00ff);
    this.addPickup('vial_1', 'health_vial', new THREE.Vector3(6, 27, -8), 0x00ffaa);
    this.addPickup('vial_2', 'health_vial', new THREE.Vector3(-6, 27, -8), 0x00ffaa);
  }

  private addPickup(
    id: string,
    type: PickupItem['type'],
    pos: THREE.Vector3,
    colorHex: number
  ) {
    const group = new THREE.Group();
    group.position.copy(pos);

    // Rotating Core Mesh
    let geo: THREE.BufferGeometry;
    if (type === 'health_pack' || type === 'armor') {
      geo = new THREE.BoxGeometry(1.2, 1.2, 1.2);
    } else if (type === 'health_vial') {
      geo = new THREE.CylinderGeometry(0.3, 0.3, 1.0, 12);
    } else {
      // Weapon Pickup: Rotating Octahedron
      geo = new THREE.OctahedronGeometry(1.0, 0);
    }

    const mat = new THREE.MeshBasicMaterial({
      color: colorHex,
      wireframe: true,
    });
    const mesh = new THREE.Mesh(geo, mat);
    group.add(mesh);

    // Soft Point Light
    const light = new THREE.PointLight(colorHex, 1.2, 6);
    group.add(light);

    this.group.add(group);
    this.pickupMeshes.push(group);

    this.pickups.push({
      id,
      type,
      position: pos.clone(),
      mesh: group,
      respawnTime: 15,
      timer: 0,
      active: true,
    });
  }

  private setupWaypointsAndSpawns() {
    // Spawns on Tower 1 (Helipad top is at y = 26.4)
    this.spawnPoints.push(new THREE.Vector3(0, 26.4, -4));
    this.spawnPoints.push(new THREE.Vector3(-5, 26.4, 0));
    this.spawnPoints.push(new THREE.Vector3(5, 26.4, 0));
    this.spawnPoints.push(new THREE.Vector3(0, 26.4, 4));

    // Spawns on Tower 2 (Open roof deck at y = 21.0, clear of shelter at x=42, z=-42)
    this.spawnPoints.push(new THREE.Vector3(54, 21.0, -42)); // East open deck
    this.spawnPoints.push(new THREE.Vector3(48, 21.0, -34)); // North open deck
    this.spawnPoints.push(new THREE.Vector3(52, 21.0, -36)); // North-East deck

    // Spawns on Tower 3 (Open roof deck at y = 17.0, clear of canopy)
    this.spawnPoints.push(new THREE.Vector3(-54, 17.0, 44)); // West open deck
    this.spawnPoints.push(new THREE.Vector3(-46, 17.0, 36)); // North open deck
    this.spawnPoints.push(new THREE.Vector3(-50, 17.0, 50)); // South-West deck

    // Bot Waypoints across the 3 towers and jump pad locations
    this.waypoints = [
      ...this.spawnPoints,
      new THREE.Vector3(0, 26.4, 0),
      new THREE.Vector3(12, 26.4, -12), // near Pad 1
      new THREE.Vector3(-12, 26.4, 12), // near Pad 2
      new THREE.Vector3(38, 21.0, -32), // near Pad 3
      new THREE.Vector3(42, 21.0, -48), // near Pad 4
      new THREE.Vector3(-36, 17.0, 34), // near Pad 5
      new THREE.Vector3(-48, 17.0, 46), // near Pad 6
      new THREE.Vector3(24, 19.0, -21), // Skybridge
    ];
  }

  private buildDistantClouds(): THREE.Points {
    const cloudGeo = new THREE.BufferGeometry();
    const count = 600;
    const pos = new Float32Array(count * 3);

    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 500;
      pos[i * 3 + 1] = -50 - Math.random() * 40;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 500;
    }

    cloudGeo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const cloudMat = new THREE.PointsMaterial({
      color: 0x4a5568,
      size: 16,
      transparent: true,
      opacity: 0.35,
    });
    const clouds = new THREE.Points(cloudGeo, cloudMat);
    this.group.add(clouds);
    return clouds;
  }

  public update(deltaTime: number) {
    this.time += deltaTime;

    // Rotate and bob pickups
    this.pickups.forEach((pickup) => {
      if (pickup.active) {
        pickup.mesh.rotation.y += deltaTime * 2;
        pickup.mesh.position.y = pickup.position.y + Math.sin(this.time * 3) * 0.25;
      } else {
        pickup.timer -= deltaTime;
        if (pickup.timer <= 0) {
          pickup.active = true;
          pickup.mesh.visible = true;
        }
      }
    });

    // Pulse Jump Pad Rings
    this.jumpPadRings.forEach((ring, idx) => {
      const pulse = 0.5 + Math.sin(this.time * 6 + idx) * 0.5;
      (ring.material as THREE.MeshBasicMaterial).opacity = 0.6 + pulse * 0.4;
    });

    // Gentle cloud drift
    this.clouds.rotation.y += deltaTime * 0.005;
  }
}
